export const GOOGLE_ANALYTICS_MEASUREMENT_ID = 'G-3EEEJXBN2S';

const ANALYTICS_HOSTNAMES = new Set(['oneultool.com', 'www.oneultool.com']);
const CAMPAIGN_QUERY_PARAMETERS = new Set([
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]);

type GoogleTag = (...args: unknown[]) => void;
type GoogleDataLayer = Array<ArrayLike<unknown>>;

declare global {
  interface Window {
    dataLayer?: GoogleDataLayer;
    gtag?: GoogleTag;
  }
}

export function createGoogleTag(dataLayer: GoogleDataLayer): GoogleTag {
  return function gtag(..._args: unknown[]): void {
    dataLayer.push(arguments);
  };
}

export function isGoogleAnalyticsEnabled(hostname: string, production: boolean): boolean {
  return production && ANALYTICS_HOSTNAMES.has(hostname);
}

export function analyticsPageLocation(value: string): string {
  const url = new URL(value);
  const campaignParameters = new URLSearchParams();
  for (const [name, parameterValue] of url.searchParams) {
    if (CAMPAIGN_QUERY_PARAMETERS.has(name)) campaignParameters.append(name, parameterValue);
  }
  const query = campaignParameters.toString();
  return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
}

export function analyticsPageReferrer(value: string): string {
  if (!value) return '';
  const url = new URL(value);
  return `${url.origin}${url.pathname}`;
}

export function initializeGoogleAnalytics(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!isGoogleAnalyticsEnabled(window.location.hostname, import.meta.env.PROD)) return;
  if (document.querySelector(`script[data-oneultool-ga="${GOOGLE_ANALYTICS_MEASUREMENT_ID}"]`)) return;

  const dataLayer = window.dataLayer ?? [];
  window.dataLayer = dataLayer;
  window.gtag ??= createGoogleTag(dataLayer);

  window.gtag('js', new Date());
  window.gtag('config', GOOGLE_ANALYTICS_MEASUREMENT_ID, {
    allow_ad_personalization_signals: false,
    allow_google_signals: false,
    page_location: analyticsPageLocation(window.location.href),
    page_referrer: analyticsPageReferrer(document.referrer),
  });

  const script = document.createElement('script');
  script.async = true;
  script.dataset.oneultoolGa = GOOGLE_ANALYTICS_MEASUREMENT_ID;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GOOGLE_ANALYTICS_MEASUREMENT_ID)}`;
  document.head.append(script);
}
